/* eslint-disable perfectionist/sort-objects */
import { createTheme, colors as MuiColors } from '@mui/material';
import { jaJP } from '@mui/material/locale';
import { jaJP as DataGridJaJP } from '@mui/x-data-grid';
import * as _ from 'lodash';

import { Gutters } from '@campaign/styling/theme.types';

const defaultBorderRadius = 8;
export const colors = {
  primary: 'rgba(227, 2, 0, 1)',
  primaryLight: 'rgba(221, 118, 117, 1)',
  primaryLight2: 'rgba(224, 1, 22, 0.07)',
  primaryLight3: 'rgba(224, 1, 22, 0.04)',
  primaryGrey600: 'rgba(71, 84, 103, 1)',
  secondary: 'rgba(223, 218, 203, 1)',
  secondaryDark: 'rgba(145, 132, 125, 1)',
  secondaryDark2: 'rgba(115, 102, 95, 1)',
  secondaryDark3: 'rgba(120, 83, 63, 1)',
  secondaryDark4: 'rgba(92, 65, 50, 1)',
  secondaryDark5: 'rgba(120, 83, 63, 0.7)',
  secondaryDark6: 'rgba(145, 132, 125, 0.5)',
  secondaryLight: 'rgba(241, 236, 228, 1)',
  secondaryLight2: 'rgba(250, 246, 238, 1)',
  secondaryLight3: 'rgba(249, 248, 246, 1)',
  secondaryLight4: 'rgba(241, 236, 228, 0.75)',
  secondaryLight5: 'rgba(241, 236, 228, 0.2)',
  secondaryLight6: 'rgba(241, 236, 228, 0.5)',
  orangeDark1: 'rgba(251, 101, 20, 1)',
  orangeLight1: 'rgba(252, 238, 222, 1)',
  blueDark1: 'rgba(2, 119, 189, 1)',
  blueLight1: 'rgba(225, 245, 254, 1)',

  tertiary: 'rgba(0, 173, 227, 1)',
  tertiaryDark: 'rgba(0, 173, 227, 0.4)',
  tertiaryLight: 'rgba(0, 173, 227, 0.08)',
  grey: {
    25: 'rgba(242, 244, 247, 1)',
    50: 'rgba(246, 248, 250, 1)',
    100: 'rgba(242, 244, 247, 1)',
    200: 'rgba(228, 231, 236, 1)',
    300: 'rgba(208, 213, 221, 1)',
    400: 'rgba(152, 162, 179, 1)',
    500: 'rgba(102, 112, 133, 1)',
    600: 'rgba(71, 84, 103, 1)',
    700: 'rgba(52, 64, 84, 1)',
    800: 'rgba(29, 41, 57, 1)',
    900: 'rgba(16, 24, 40, 1)',
  },
  error: {
    25: '#FFFBFA',
    50: '#FEF3F2',
    100: '#FEE4E2',
    200: '#FECDCA',
    300: '#FDA29B',
    400: '#F97066',
    500: '#F04438',
    600: '#475467',
    700: '#B42318',
    800: '#912018',
    900: '#7A271A',
  },
  whiteBackdrop: 'rgba(256,256,256,0.4)',
};

const designUnit = 8;
const guttersValues = {
  xxxxxlg: designUnit * 16,
  xxxxlg: designUnit * 8,
  xxxlg: designUnit * 7,
  xxlg: designUnit * 6,
  xlg: designUnit * 5,
  lg: designUnit * 4,
  md: designUnit * 3,
  sm: designUnit * 2.5,
  xsm: designUnit * 2,
  xxsm: designUnit * 1.75,
  xxxsm: designUnit * 1.5,
  xxxxsm: designUnit * 1.25,
  xxxxxsm: designUnit,
  xxxxxxsm: 0,
};

const gutters: Gutters = {
  xxxxxlg: 13,
  xxxxlg: 12,
  xxxlg: 11,
  xxlg: 10,
  xlg: 9,
  lg: 8,
  md: 7,
  sm: 6,
  xsm: 5,
  xxsm: 4,
  xxxsm: 3,
  xxxxsm: 2,
  xxxxxsm: 1,
  xxxxxxsm: 0,
};

export const theme = createTheme(
  {
    palette: {
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        contrastText: MuiColors.common.white,
      },
      secondary: {
        main: colors.secondary,
        light: colors.secondaryLight,
        dark: colors.secondaryDark,
        contrastText: colors.grey[800],
      },
      grey: MuiColors.grey,
      background: {
        default: MuiColors.common.white,
      },
      text: {
        primary: colors.grey[800],
        secondary: colors.grey[600],
        disabled: colors.grey[300],
      },
    },
    colors,
    gutters,
    spacing: _.valuesIn(guttersValues).reverse(),
    typography: {
      fontFamily: ['YuGothic', 'Roboto', 'Helvetica', 'Arial'].join(','),
      xxxxl: {
        fontSize: '30px',
        fontWeight: 700,
      },
      xxxl: {
        fontSize: '24px',
        fontWeight: 700,
      },
      xxl: {
        fontSize: '20px',
        fontWeight: 700,
      },
      xl: {
        fontSize: '20px',
        fontWeight: 700,
      },
      lg: {
        fontSize: '18px',
        fontWeight: 700,
      },
      md: {
        fontSize: '16px',
        fontWeight: 500,
      },
      mdBold: {
        fontSize: '16px',
        fontWeight: 700,
      },
      sm: {
        fontSize: '15px',
        fontWeight: 500,
      },
      smBold: {
        fontSize: '15px',
        fontWeight: 700,
      },
      xs: {
        fontSize: '14px',
        fontWeight: 500,
      },
      xsBold: {
        fontSize: '14px',
        fontWeight: 700,
      },
      xxs: {
        fontSize: '13px',
        fontWeight: 500,
      },
      xxsBold: {
        fontSize: '13px',
        fontWeight: 700,
      },
      xxxs: {
        fontSize: '12px',
        fontWeight: 500,
      },
      xxxsBold: {
        fontSize: '12px',
        fontWeight: 700,
      },
    },
    components: {
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            margin: theme.spacing(theme.gutters.sm, 0),
            borderColor: theme.colors.secondaryLight,
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            outline: 'none',
            borderRadius: defaultBorderRadius,
            border: '2px solid',
            borderColor: theme.colors.secondary,
            '&.Mui-focused': {
              color: MuiColors.grey[900],
            },
          }),
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            margin: 0,
            width: '100%',
            background: 'transparent',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            borderRadius: 6,
            margin: 0,
            minWidth: 36,
            maxHeight: '26px',
            fontSize: '13px',
            background: theme.colors.secondaryLight4,
            color: theme.colors.secondaryDark3,
            '& .MuiChip-label': {
              ...theme.typography.xs,
              padding: `0px 6px`,
              fontWeight: 400,
              margin: 0,
              letterSpacing: '-1px',
            },
            ...(ownerState.variant === 'orange' && {
              background: theme.colors.orangeLight1,
              color: theme.colors.orangeDark1,
            }),
            ...(ownerState.variant === 'blue' && {
              background: theme.colors.blueLight1,
              color: theme.colors.blueDark1,
            }),
          }),
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          input: ({ theme }) => ({
            padding: theme.spacing(theme.gutters.xsm, theme.gutters.xsm),
            height: 48 - 16 * 2,
            fontSize: theme.typography.sm.fontSize,
          }),
          root: ({ theme }) => ({
            borderRadius: defaultBorderRadius,
            border: '1px solid',
            borderColor: theme.colors.secondary,
            '&.Mui-focused': {
              boxShadow: 'none',
              border: '1px solid',
              borderColor: theme.colors.secondaryDark,
            },
          }),
          notchedOutline: {
            border: 'transparent',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          ul: {
            paddingLeft: theme.spacing(theme.gutters.xsm),
          },
        }),
      },
      MuiSelect: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            padding: theme.spacing(theme.gutters.xsm, theme.gutters.xsm),
            height: 48 - 16 * 2,
            fontSize: theme.typography.sm.fontSize,
            minHeight: 'auto',
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&$focusVisible': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-selected': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-colorPrimary': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&:hover': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-selected:hover': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-focused': {
              backgroundColor: theme.colors.secondaryLight,
            },
          }),
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          menuItem: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-colorPrimary': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&:hover': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-selected:hover': {
              backgroundColor: theme.colors.secondaryLight,
            },
            '&.Mui-focused': {
              backgroundColor: theme.colors.secondaryLight,
            },
          }),
        },
      },
      MuiTabs: {
        variants: [
          {
            props: { indicatorColor: 'primaryColor' },
            style: ({ theme }) => ({
              '.MuiTabs-indicator': {
                backgroundColor: theme.colors.primary,
              },
            }),
          },
        ],
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ theme }) => ({
            marginLeft: 0,
          }),
        },
      },
      // slight css issue on the box around select
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: theme.palette?.common?.white,
            '& .MuiSelect-select': {
              borderColor: theme.colors.secondary,
            },
            '& .MuiSelect-select .MuiTypography-root': {
              lineHeight: 1.3,
              display: 'block',
            },
            '> .MuiInputBase-multiline': {
              padding: '0px',
            },
          }),
        },
      },
      MuiTab: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          textColorPrimary: ({ theme }) => ({
            color: MuiColors.grey[900],
            fontSize: theme.typography.md.fontSize,
            padding: theme.spacing(theme.gutters.xsm, theme.gutters.xxxxxsm),
            marginLeft: theme.spacing(theme.gutters.md),
            marginRight: theme.spacing(theme.gutters.md),
            '&.Mui-selected': {
              fontWeight: 'bold',
              color: MuiColors.grey[900],
            },
          }),
          textColorSecondary: () => ({
            color: MuiColors.grey[900],
          }),
        },
      },

      MuiButton: {
        defaultProps: {
          disableRipple: true,
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            padding: theme.spacing(theme.gutters.xxxxxsm, theme.gutters.xsm),
            color: theme.palette?.text?.primary,
            borderRadius: 6,
            boxShadow: 'none',
            height: 38,
            lineHeight: 1,
            '&.Mui-disabled': {
              color: MuiColors.common.white,
              background: theme.colors.grey[300],
              border: `1px solid ${theme.colors.grey[300]}`,
            },
            ...(ownerState.variant === 'primary' && {
              fontWeight: 600,
              fontSize: theme.typography.sm.fontSize,
              background: theme.colors.primary,
              color: MuiColors.common.white,
              '&:hover': {
                background: theme.colors.primaryLight,
              },
            }),
            ...(ownerState.variant === 'tab' && {
              borderBottom: '2px solid white',
              borderRadius: 0,
              marginRight: theme.spacing(theme.gutters.lg),
              minHeight: 54,
              '&.Mui-selected': {
                borderBottom: `2px solid ${theme.colors.primary}`,
              },
            }),
            ...(ownerState.variant === 'secondary' && {
              fontWeight: 300,
              color: theme.palette?.text?.primary,
              background: MuiColors.common.white,
              border: `1px solid ${theme.colors.secondary}`,
              '&:hover': {
                color: theme.palette?.text?.primary,
                background: theme.colors.secondaryLight5,
                borderColor: theme.colors.secondaryDark,
              },
            }),
            ...(ownerState.variant === 'tertiary' && {
              fontWeight: 300,
              color: colors.primary,
              background: MuiColors.common.white,
              border: `1px solid ${theme.colors.secondary}`,
            }),
            ...(ownerState.variant === 'text' && {
              fontWeight: 300,
              color: theme.palette?.text?.secondary,
              '&.Mui-disabled': {
                background: 'transparent',
                opacity: 0.5,
              },
              '&:hover': {
                background: 'transparent',
                color: theme.palette?.text?.primary,
              },
            }),
          }),
        },
      },
      MuiGrid2: {
        defaultProps: {
          disableEqualOverflow: true,
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&$checked': {
              color: theme.colors.primary,
            },
          }),
          checked: ({ theme }) => ({
            color: theme.colors.primary,
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            boxShadow: '0px 2px 2px 0px rgba(107, 92, 98, 0.20)',
            transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            borderRadius: 4,
            ...(ownerState.variant === 'modal' && {
              borderRadius: 4,
              padding: theme.spacing(theme.gutters.lg, theme.gutters.lg),
              left: '50%',
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }),
            ...(ownerState.variant === 'page' && {
              borderRadius: 4,
              padding: theme.spacing(theme.gutters.lg, theme.gutters.md),
              margin: theme.spacing(theme.gutters.lg, 0, 0, 0),
              minWidth: '100%',
            }),
            ...(ownerState.variant === 'pageNoGutter' && {
              borderRadius: 4,
              padding: theme.spacing(theme.gutters.md, 0),
              margin: theme.spacing(theme.gutters.md, 0, 0, 0),
            }),
            ...(ownerState.variant === 'pageNoSpacing' && {
              padding: 0,
              margin: 0,
              borderRadius: 6,
              overflow: 'hidden',
            }),
            ...(ownerState.variant === 'bar' && {
              borderRadius: 0,
              padding: theme.spacing(
                theme.gutters.xsm,
                theme.gutters.sm,
                theme.gutters.xsm,
                theme.gutters.sm
              ),
            }),
          }),
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: ({ theme }) => ({}),
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: ({ theme }) => ({
            margin: theme.spacing(theme.gutters.lg, 0),
            color: MuiColors.common.black,
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 0,
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 0,
            marginBottom: theme.spacing(theme.gutters.xsm),
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            minWidth: 740,
            padding: theme.spacing(theme.gutters.xlg, theme.gutters.lg),
          }),
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '30px',
            backgroundColor: theme.colors.secondaryDark4,
            fontWeight: 800,
          }),
          message: ({ theme }) => ({
            width: '100%',
            padding: theme.spacing(0, theme.gutters.xsm),
            margin: 0,
            textAlign: 'center',
          }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          main: ({ theme }) => ({
            minHeight: 172, //one row
          }),
          columnHeader: ({ theme }) => ({
            padding: 0,
            margin: 0,
          }),
          columnHeaders: ({ theme }) => ({
            fontSize: theme.typography.smBold.fontSize,
            borderRadius: 0,
            background: theme.colors.secondaryLight,
            padding: 0,
            margin: 0,
          }),
          checkboxInput: ({ theme }) => ({
            color: theme.colors.secondaryDark,
            '&.Mui-checked': {
              color: theme.colors.primary,
            },
          }),
          columnHeaderTitle: ({ theme }) => ({
            fontWeight: 'bold',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            lineHeight: 1,
            padding: theme.spacing(theme.gutters.md, theme.gutters.md),
          }),
          columnSeparator: {
            display: 'none',
          },
          cell: ({ theme }) => ({
            minHeight: 100,
            fontSize: theme.typography.sm.fontSize,
            padding: theme.spacing(theme.gutters.xxsm, theme.gutters.md),
          }),
          cellContent: ({ theme }) => ({
            overflow: 'hidden',
          }),
          row: ({ theme }) => ({
            cursor: 'pointer',
            '&.Mui-selected': {
              backgroundColor: theme.colors.secondaryLight4,
            },
            '&.Mui-selected:hover': {
              backgroundColor: theme.colors.secondaryLight4,
            },
            '.MuiDataGrid-cell ul': {
              listStyle: 'none',
              margin: 0,
              padding: 0,
            },
          }),
          footerContainer: ({ theme }) => ({
            width: '100%',
            padding: theme.spacing(
              theme.gutters.md,
              theme.gutters.md,
              theme.gutters.xxlg,
              theme.gutters.md
            ),
            '& nav': {
              width: '100%',
            },
            '& ul': {
              justifyContent: 'start',
              width: '100%',
              '& li': {
                '& button': {
                  padding: theme.spacing(
                    theme.gutters.xxxxxsm,
                    theme.gutters.xsm
                  ),
                  height: 38,
                  lineHeight: 1,
                  color: theme.palette?.text?.primary,
                  fontWeight: 300,
                  '&.Mui-selected': {
                    background: theme.colors.secondaryLight,
                  },
                  '&:hover': {
                    color: theme.palette?.text?.primary,
                    background: theme.colors.secondaryLight5,
                    borderColor: theme.colors.secondaryDark,
                  },
                },
                '&:first-of-type': {
                  flex: 'auto',
                  '& button': {
                    border: `1px solid ${theme.colors.secondary}`,
                  },
                },
                '&:last-of-type': {
                  flex: 'auto',
                  '& button': {
                    float: 'right',
                    border: `1px solid ${theme.colors.secondary}`,
                  },
                },
              },
            },
            '.MuiDataGrid-selectedRowCount': {
              display: 'none',
            },
          }),
        },
      },
    },
  },
  jaJP,
  DataGridJaJP
);
